// src/contexts/ThemeContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const THEMES = {
  dark: {
    name: 'dark',
    // Backgrounds
    bgApp:       '#080d1a',
    bgSidebar:   '#0d1326',
    bgCard:      '#0d1326',
    bgInput:     '#090e1a',
    bgRow:       'rgba(255,255,255,0.02)',
    bgHover:     'rgba(255,255,255,0.03)',
    bgActive:    'rgba(249,115,22,0.07)',
    bgBadge:     'rgba(255,255,255,0.06)',
    bgModal:     '#0d1326',
    bgOverlay:   'rgba(0,0,0,0.7)',
    bgTableHead: '#090e1a',
    bgTag:       '#1a2340',
    // Borders
    border:      '#1a2340',
    borderInput: '#1e2a4a',
    borderFocus: '#f97316',
    // Text
    textPrimary:   '#e8eaf0',
    textSecondary: '#a0aec0',
    textMuted:     '#6b7a9a',
    textFaint:     '#4a5a7a',
    // Sidebar
    sidebarText:   '#6b7a9a',
    sidebarActive: '#e8eaf0',
    // Misc
    shadow:      '0 40px 80px rgba(0,0,0,0.5)',
    shadowCard:  '0 4px 24px rgba(0,0,0,0.3)',
    gridLine:    'rgba(249,115,22,0.04)',
    scrollbar:       '#1e2a4a',
    scrollbarTrack:  '#0d1326',
  },
  light: {
    name: 'light',
    // Backgrounds
    bgApp:       '#f1f4f9',
    bgSidebar:   '#ffffff',
    bgCard:      '#ffffff',
    bgInput:     '#f8fafc',
    bgRow:       'rgba(0,0,0,0.015)',
    bgHover:     'rgba(0,0,0,0.025)',
    bgActive:    'rgba(249,115,22,0.06)',
    bgBadge:     'rgba(0,0,0,0.05)',
    bgModal:     '#ffffff',
    bgOverlay:   'rgba(0,0,0,0.45)',
    bgTableHead: '#f8fafc',
    bgTag:       '#e8edf5',
    // Borders
    border:      '#e2e8f0',
    borderInput: '#cbd5e1',
    borderFocus: '#f97316',
    // Text
    textPrimary:   '#0f172a',
    textSecondary: '#334155',
    textMuted:     '#64748b',
    textFaint:     '#94a3b8',
    // Sidebar
    sidebarText:   '#64748b',
    sidebarActive: '#0f172a',
    // Misc
    shadow:      '0 40px 80px rgba(0,0,0,0.12)',
    shadowCard:  '0 2px 12px rgba(0,0,0,0.08)',
    gridLine:    'rgba(0,0,0,0.03)',
    scrollbar:       '#cbd5e1',
    scrollbarTrack:  '#f1f4f9',
  },
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeName, setThemeName] = useState(() => {
    return localStorage.getItem('sdt_theme') || 'dark';
  });

  const theme = THEMES[themeName] || THEMES.dark;

  const toggleTheme = () => {
    const next = themeName === 'dark' ? 'light' : 'dark';
    setThemeName(next);
    localStorage.setItem('sdt_theme', next);
  };

  // Inject CSS variables + scrollbar styles into :root
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });
    // Body background
    document.body.style.background = theme.bgApp;
    document.body.style.color = theme.textPrimary;

    // Inject scrollbar styles
    const styleId = 'sdt-scrollbar-style';
    let el = document.getElementById(styleId);
    if (!el) { el = document.createElement('style'); el.id = styleId; document.head.appendChild(el); }
    el.textContent = `
      ::-webkit-scrollbar { width: 6px; height: 6px; }
      ::-webkit-scrollbar-track { background: ${theme.scrollbarTrack}; }
      ::-webkit-scrollbar-thumb { background: ${theme.scrollbar}; border-radius: 3px; }
      ::-webkit-scrollbar-thumb:hover { background: ${theme.borderInput}; }
      tr:hover td { background: ${theme.bgRow} !important; }
    `;
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, themeName, toggleTheme, isDark: themeName === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be inside ThemeProvider');
  return ctx;
};

export { THEMES };
